export const emptyPipelineTenderForm = () => ({
  reference: '',
  title: '',
  organization: '',
  location: '',
  description: '',
  estimatedValue: '',
  currency: 'USD',
  deadline: '',
  tenderType: '',
  therapeuticArea: '',
  source: '',
  serviceType: '',
  sector: '',
  tags: [],
  urgency: 'medium',
  priority: 'medium',
  pipelineStage: 'identified',
  workflowStage: 'identified',
  participationMode: '',
  financialOpeningAnnounced: '',
  bidValidityDays: '',
  bidValidityReference: '',
  winProbability: 50
})

const localDateTime = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (part) => String(part).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const mapTenderToForm = (tender) => ({
  reference: tender.reference || '',
  title: tender.title || '',
  organization: tender.organization || '',
  location: tender.location || '',
  description: tender.description || '',
  estimatedValue: tender.estimatedValue ?? '',
  currency: tender.currency || 'USD',
  deadline: tender.deadline ? new Date(tender.deadline).toISOString().split('T')[0] : '',
  tenderType: tender.tenderType || '',
  therapeuticArea: tender.therapeuticArea || '',
  source: tender.source || '',
  serviceType: tender.serviceType || '',
  sector: tender.sector || '',
  tags: tender.tags || [],
  urgency: tender.urgency || 'medium',
  priority: tender.priority || 'medium',
  pipelineStage: tender.pipelineStage || 'identified',
  workflowStage: tender.workflowStage || tender.pipelineStage || 'identified',
  closingOutcome: tender.closingOutcome || '',
  outcomeReasonCode: tender.outcomeReasonCode || '',
  outcomeReasonLabel: tender.outcomeReasonLabel || '',
  outcomeRemarks: tender.outcomeRemarks || '',
  winProbability: tender.winProbability ?? '',
  submissionDate: tender.submissionDate || (tender.deadline ? String(tender.deadline).slice(0, 10) : ''),
  submissionTime: tender.deadlineTimeKnown ? (tender.submissionTime || '') : '',
  deadlineTimezone: tender.deadlineTimezone || 'Asia/Kolkata',
  deadlineTimeKnown: tender.deadlineTimeKnown === true,
  timeConfirmationNeeded: tender.timeConfirmationNeeded === true || tender.deadlineTimeKnown !== true,
  sourceUrl: tender.sourceUrl || '',
  ownerId: (() => {
    const raw = tender.ownerId || tender.owner?.id || tender.owner?._id || ''
    return raw ? String(raw) : ''
  })(),
  teamMemberIds: (tender.teamMemberIds || []).map((id) => String(id?._id || id)),
  publicationDate: tender.publicationDate ? String(tender.publicationDate).slice(0, 10) : '',
  preBidMeetingAt: tender.preBidMeetingAt ? String(tender.preBidMeetingAt).slice(0, 16) : '',
  queryDeadline: tender.queryDeadline ? String(tender.queryDeadline).slice(0, 16) : '',
  technicalOpeningAt: tender.technicalOpeningAt ? localDateTime(tender.technicalOpeningAt) : '',
  financialOpeningAt: tender.financialOpeningAnnounced === true && tender.financialOpeningAt ? localDateTime(tender.financialOpeningAt) : '',
  financialOpeningAnnounced: tender.financialOpeningAnnounced === true ? 'announced' : tender.financialOpeningAnnounced === false ? 'not_announced' : '',
  bidValidityDays: tender.bidValidityDays ?? '',
  bidValidityReference: tender.bidValidityReference || '',
  bidValidityReferenceDate: tender.bidValidityReferenceDate ? String(tender.bidValidityReferenceDate).slice(0, 10) : '',
  bidValidityExpiresAt: tender.bidValidityExpiresAt || '',
  participationMode: tender.participationMode || '',
  participationApprovedMode: tender.participationApprovedMode || '',
  participationChangeReason: '',
  lifecycle: tender.lifecycle || null,
  projectValue: tender.projectValue ?? '',
  projectValueStatus: tender.projectValueStatus || 'unknown',
  consultancyFee: tender.consultancyFee ?? (tender.consultancyFeeStatus === 'stated' ? tender.estimatedValue ?? '' : ''),
  consultancyFeeStatus: tender.consultancyFeeStatus || (tender.estimatedValue == null ? 'unknown' : 'stated')
})

export const validatePipelineTenderForm = (formData, { workspace = false } = {}) => {
  const errors = {}
  if (!formData.reference?.trim()) errors.reference = 'Reference is required'
  if (!formData.title?.trim()) errors.title = 'Title is required'
  if (!formData.organization?.trim()) errors.organization = workspace ? 'Client is required' : 'Organization is required'
  if (!formData.location?.trim()) errors.location = 'Location is required'
  if (!formData.description?.trim()) errors.description = 'Description is required'
  if (workspace) {
    if (!formData.submissionDate && !formData.deadline) errors.submissionDate = 'Submission date is required'
    if (formData.projectValueStatus === 'stated' && formData.projectValue === '') errors.projectValue = 'Enter the project value or mark it unknown or not disclosed'
    if ((formData.consultancyFeeStatus || 'stated') === 'stated' && formData.consultancyFee === '' && formData.estimatedValue === '') {
      errors.consultancyFee = 'Enter the consultancy fee or mark it unknown or not disclosed'
    }
  } else if (!formData.estimatedValue) errors.estimatedValue = 'Estimated value is required'
  if (!workspace && !formData.deadline) errors.deadline = 'Deadline is required'
  if (!formData.tenderType) errors.tenderType = 'Tender type is required'
  if (!workspace && !formData.therapeuticArea) errors.therapeuticArea = 'Therapeutic area is required'
  if (!formData.source?.trim()) errors.source = 'Source is required'
  return errors
}

export const formatPipelineCurrency = (amount, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(amount || 0))

export const formatPipelineDate = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

export const formatPipelineLabel = (value) => {
  if (!value) return '—'
  return String(value)
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}
